import React, { useState, useEffect, useRef } from 'react';
import {
  Search, Printer, X, Eye, FileText, Package, Loader2,
  BookOpen, Plus, Trash2, CheckCircle, AlertCircle, Download,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Filter
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';

export default function ReceivingReports() {
  const [grList, setGrList] = useState([]);
  const [filteredGRs, setFilteredGRs] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedGR, setSelectedGR] = useState(null);
  const [rrData, setRRData] = useState(null);
  const [loadingRR, setLoadingRR] = useState(false);
  const [vendorList, setVendorList] = useState([]);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const printRef = useRef();

  const [showJEModal, setShowJEModal] = useState(false);
  const [savingJE, setSavingJE] = useState(false);
  const [jeLines, setJeLines] = useState([]);
  const [jeStatusMap, setJeStatusMap] = useState({});
  const [savedJECode, setSavedJECode] = useState('—');

  // ── Pagination ──
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => { fetchGRList(); fetchVendorList(); }, []);

  useEffect(() => {
    if (search.trim() === '') {
      setFilteredGRs(grList);
    } else {
      const q = search.toLowerCase();
      setFilteredGRs(grList.filter(gr =>
        gr.grNo?.toLowerCase().includes(q) ||
        resolveVendorName(gr).toLowerCase().includes(q) ||
        gr.vendorName?.toLowerCase().includes(q) ||
        gr.poNumber?.toLowerCase().includes(q)
      ));
    }
    setCurrentPage(1);
  }, [search, grList]);

  const totalPages = Math.ceil(filteredGRs.length / itemsPerPage);
  const paginatedGRs = filteredGRs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const fetchVendorList = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Vendor`);
      const data = await res.json();
      setVendorList(Array.isArray(data) ? data : []);
    } catch (err) { console.error('Error fetching vendors:', err); }
  };

  const resolveVendorName = (gr) => {
    const match =
      vendorList.find(v => v.VendorCode === gr.vendorCode) ||
      vendorList.find(v => v.id === gr.vendorId) ||
      null;
    return match?.VendorName || gr.vendorName || '—';
  };

  const fetchGRList = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/GR_Header`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setGrList(list);
      setFilteredGRs(list);
      fetchAllJEStatuses(list);
    } catch (err) { console.error('Error fetching GRs:', err); }
    finally { setLoading(false); }
  };

  const fetchAllJEStatuses = async (list) => {
    try {
      const res = await fetch(`${API_BASE_URL}api/JournalEntry`);
      const allJE = await res.json();
      if (!Array.isArray(allJE)) return;
      const map = {};
      list.forEach(gr => { map[gr.grNo] = allJE.some(je => je.gr_No === gr.grNo); });
      setJeStatusMap(map);
    } catch (err) { console.error('JE status fetch failed:', err); }
  };

  const normalizeJELine = (je) => ({
    Account_No: je.account_No ?? je.Account_No ?? je.accountNo ?? '',
    Account_Name: je.account_Name ?? je.Account_Name ?? je.accountName ?? '',
    Debit: parseFloat(je.debit ?? je.Debit) || 0,
    Credit: parseFloat(je.credit ?? je.Credit) || 0,
  });

  const handleViewRR = async (gr) => {
    setLoadingRR(true);
    setSelectedGR(gr);
    setSavedJECode('—');
    try {
      const grRes = await fetch(`${API_BASE_URL}api/GR_Header/${gr.id}`);
      const grFull = await grRes.json();

      let poDetails = [], poHeader = null;
      if (grFull.poNumber) {
        try {
          const poRes = await fetch(`${API_BASE_URL}api/T_PO_Header`);
          const poList = await poRes.json();
          poHeader = poList.find(p => p.poNumber === grFull.poNumber);
          if (poHeader) {
            const poDetRes = await fetch(`${API_BASE_URL}api/T_Po_Details/POHeader/${poHeader.id}`);
            poDetails = await poDetRes.json();
            if (!Array.isArray(poDetails)) poDetails = [poDetails];
          }
        } catch (e) { console.warn('PO fetch failed:', e); }
      }

      let vendorInfo = null;
      try {
        const vendorRes = await fetch(`${API_BASE_URL}api/Vendor`);
        const vList = await vendorRes.json();
        if (Array.isArray(vList)) {
          vendorInfo =
            vList.find(v => v.VendorCode === grFull.vendorCode) ||
            vList.find(v => v.id === grFull.vendorId) ||
            vList.find(v => v.VendorName?.toLowerCase() === grFull.vendorName?.toLowerCase()) || null;
        }
      } catch (e) { console.warn('Vendor fetch failed:', e); }

      const mergedItems = grFull.grDetails?.map((grItem, idx) => {
        const poItem = poDetails.find(p => p.itemCode === grItem.itemCode) || {};
        const grossPrice = poItem.unitPrice || grItem.unitPrice || 0;
        const pcs = grItem.receivedQty || 0;
        const grossAmt = grossPrice * pcs;
        const vatAmt = grItem.vatAmount || 0;
        const disc = grItem.discount || 0;
        const totalAmt = grossAmt - disc + vatAmt;
        return {
          no: idx + 1,
          itemCode: grItem.itemCode,
          itemDescription: grItem.itemDescription,
          poQty: grItem.orderedQty || poItem.qty || 0,
          uom: grItem.uom || 'PC',
          grossPrice, amtWoVat: grossAmt, vatAmt, disc, totalAmt,
          qtyRcvd: pcs,
          qtyDiff: (poItem.qty || grItem.orderedQty || 0) - pcs,
        };
      }) || [];

      const totalAmount = mergedItems.reduce((sum, i) => sum + i.totalAmt, 0);

      let savedJELines = [];
      try {
        const jeRes = await fetch(`${API_BASE_URL}api/JournalEntry`);
        const allJE = await jeRes.json();
        if (Array.isArray(allJE)) {
          savedJELines = allJE.filter(je => je.gr_No === grFull.grNo);
          if (savedJELines.length > 0) setSavedJECode(savedJELines[0].jE_Code_No ?? '—');
        }
      } catch (e) { console.warn('JE fetch failed:', e); }

      setRRData({ grFull, poHeader, vendorInfo, mergedItems, totalAmount, savedJELines });
    } catch (err) {
      console.error('Error building RR:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load RR data', confirmButtonColor: '#E24B4A' });
    } finally { setLoadingRR(false); }
  };

  // ===== EXPORT PDF =====
  const handleExportPDF = async () => {
    if (!rrData) return;
    setExportingPDF(true);
    try {
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
      const { jsPDF } = window.jspdf;

      const mergedItemsHTML = rrData.mergedItems.map((item, i) => `
        <tr style="background:${i % 2 === 0 ? '#fff' : '#f9f9f9'}">
          <td style="border:1px solid #ccc;padding:4px 6px;font-family:monospace">${item.itemCode}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;text-transform:uppercase">${item.itemDescription}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;text-align:center">${formatCurrency(item.poQty)}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;text-align:center;text-transform:uppercase">${item.uom}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;text-align:right">${formatCurrency(item.grossPrice)}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;text-align:right">${formatCurrency(item.amtWoVat)}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;text-align:right">${formatCurrency(item.vatAmt)}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;text-align:right">${formatCurrency(item.disc)}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;text-align:right;font-weight:600">${formatCurrency(item.totalAmt)}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;text-align:center">${formatCurrency(item.qtyRcvd)}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;text-align:center">${formatCurrency(item.qtyDiff)}</td>
        </tr>`).join('');

      const jeRowsHTML = rrData.savedJELines?.length > 0
        ? rrData.savedJELines.map(je => {
            const n = normalizeJELine(je);
            return `<tr><td style="padding:3px 6px;border-bottom:1px solid #eee">${n.Account_No}</td><td style="padding:3px 6px;border-bottom:1px solid #eee">${n.Account_Name}</td><td style="padding:3px 6px;border-bottom:1px solid #eee;text-align:right">${formatCurrency(n.Debit)}</td><td style="padding:3px 6px;border-bottom:1px solid #eee;text-align:right">${formatCurrency(n.Credit)}</td></tr>`;
          }).join('')
        : `<tr><td colspan="4" style="padding:8px;text-align:center;color:#aaa;font-style:italic">No Journal Entry generated yet.</td></tr>`;

      const jeTotalHTML = rrData.savedJELines?.length > 0 ? `
        <tr style="border-top:1px solid #000;font-weight:bold">
          <td colspan="2" style="padding:4px 6px">TOTAL</td>
          <td style="padding:4px 6px;text-align:right">${formatCurrency(jeDisplayTotal('Debit'))}</td>
          <td style="padding:4px 6px;text-align:right">${formatCurrency(jeDisplayTotal('Credit'))}</td>
        </tr>` : '';

      const signatories = [
        { label: 'Prepared By:', name: getReceivedBy() },
        { label: 'Noted By:', name: '-' },
        { label: 'Checked By:', name: '-' },
        { label: 'Approved By:', name: '-' },
      ];

      const signaturesHTML = signatories.map(sig => `
        <div style="width:25%;text-align:center;padding:0 8px;box-sizing:border-box">
          <div style="text-align:left;font-weight:bold;margin-bottom:40px;font-size:9px">${sig.label}</div>
          <div style="font-weight:600;font-size:9px;margin-bottom:4px">${sig.name || ''}</div>
          <div style="border-top:1px solid #000;padding-top:4px;font-weight:bold;font-size:9px">${sig.name || '\u00A0'}</div>
          <div style="color:#666;font-size:8px">${sig.label.replace(':', '')}</div>
        </div>`).join('');

      const htmlContent = `
        <div style="font-family:Arial,sans-serif;font-size:10px;color:#000;background:#fff;padding:32px;width:900px;box-sizing:border-box">
          <div style="text-align:center;margin-bottom:12px">
            <div style="font-size:13px;font-weight:900;text-transform:uppercase;letter-spacing:1px">MEGA SOFT HYGIENIC PRODUCTS, INC.</div>
            <div style="font-size:9px;color:#555;margin-top:2px">Rm202 Mega One Building España Blvd Cor. Mayon St. Brgy Sta. Teresita Quezon City</div>
            <div style="font-size:9px;color:#555">Tel No.: 8363-4851 / 8363-4854 / 8363-4853 / 8363-4855</div>
            <div style="font-size:9px;color:#555">Vat Reg.: 206-227-179-000</div>
          </div>
          <div style="text-align:right;font-size:9px;font-weight:bold;margin-bottom:2px">RR No.: ${rrData.grFull.grNo}</div>
          <div style="text-align:center;font-size:12px;font-weight:bold;text-transform:uppercase;text-decoration:underline;letter-spacing:2px;margin-bottom:12px">RECEIVING REPORT</div>
          <table style="width:100%;border-collapse:collapse;border:1px solid #000;margin-bottom:14px;font-size:9px">
            <tr>
              <td style="width:50%;padding:10px 12px;border-right:1px solid #000;vertical-align:top">
                <table style="width:100%;border-collapse:collapse">
                  <tr><td style="font-weight:bold;white-space:nowrap;padding:2px 0;width:95px">Vendor Name:</td><td style="border-bottom:1px solid #999;padding:2px 4px">${getVendorName()}</td></tr>
                  <tr><td style="font-weight:bold;white-space:nowrap;padding:2px 0;width:95px">Vendor TIN:</td><td style="border-bottom:1px solid #999;padding:2px 4px">${getVendorTin()}</td></tr>
                  <tr><td style="font-weight:bold;white-space:nowrap;padding:2px 0;width:95px">Vendor Address:</td><td style="border-bottom:1px solid #999;padding:2px 4px">${getVendorAddress()}</td></tr>
                  <tr><td style="font-weight:bold;white-space:nowrap;padding:2px 0;width:95px">Ship To:</td><td style="border-bottom:1px solid #999;padding:2px 4px">${getShipTo() || '&nbsp;'}</td></tr>
                </table>
              </td>
              <td style="width:50%;padding:10px 12px;vertical-align:top">
                <table style="width:100%;border-collapse:collapse">
                  <tr><td style="font-weight:bold;white-space:nowrap;padding:2px 0;width:105px">PO No.:</td><td style="border-bottom:1px solid #999;padding:2px 4px">${rrData.grFull.poNumber ? `Based On PO ${rrData.grFull.poNumber}` : '—'}</td></tr>
                  <tr><td style="font-weight:bold;white-space:nowrap;padding:2px 0;width:105px">SI No.:</td><td style="border-bottom:1px solid #999;padding:2px 4px">${getSINo()}</td></tr>
                  <tr><td style="font-weight:bold;white-space:nowrap;padding:2px 0;width:105px">PO Date:</td><td style="border-bottom:1px solid #999;padding:2px 4px">${formatDate(getPoDate())}</td></tr>
                  <tr><td style="font-weight:bold;white-space:nowrap;padding:2px 0;width:105px">Document Date:</td><td style="border-bottom:1px solid #999;padding:2px 4px">${formatDate(rrData.grFull.grDate)}</td></tr>
                  <tr><td style="font-weight:bold;white-space:nowrap;padding:2px 0;width:105px">Posting Date:</td><td style="border-bottom:1px solid #999;padding:2px 4px">${formatDate(rrData.grFull.postingDate || rrData.grFull.grDate)}</td></tr>
                </table>
              </td>
            </tr>
          </table>
          <table style="width:100%;border-collapse:collapse;border:1px solid #ccc;font-size:9px;margin-bottom:6px">
            <thead><tr style="background:#efefef">${['Item Code','Item Description','PO Qty','UoM','Gross Price','Amt w/o Vat','Vat Amt','Disc','Total Amt','Qty Rcvd','Qty Diff'].map(h => `<th style="border:1px solid #bbb;padding:5px 6px;text-align:center;font-weight:600;white-space:nowrap">${h}</th>`).join('')}</tr></thead>
            <tbody>${mergedItemsHTML}</tbody>
          </table>
          <div style="text-align:center;font-weight:bold;font-size:9px;margin:8px 0">****NOTHING FOLLOWS***</div>
          <div style="display:flex;justify-content:flex-end;gap:40px;border-top:1px solid #000;padding-top:6px;margin-bottom:14px;font-size:9px;font-weight:bold">
            <span style="text-decoration:underline">Total Amount</span><span>${formatCurrency(rrData.totalAmount)}</span>
          </div>
          <div style="border:1px solid #000;padding:10px 12px;margin-bottom:14px;font-size:9px">
            <div style="display:flex;justify-content:space-between;margin-bottom:8px">
              <span style="font-weight:bold">JE No. ${savedJECode}</span>
              <span style="font-weight:bold;flex:1;text-align:center">Journal Entry</span>
            </div>
            <table style="width:100%;border-collapse:collapse;font-size:9px">
              <thead><tr style="border-bottom:1px solid #000"><th style="text-align:left;padding:4px 6px;font-weight:bold">Account</th><th style="text-align:left;padding:4px 6px;font-weight:bold">Account Name</th><th style="text-align:right;padding:4px 6px;font-weight:bold">Debit</th><th style="text-align:right;padding:4px 6px;font-weight:bold">Credit</th></tr></thead>
              <tbody>${jeRowsHTML}</tbody>
              <tfoot>${jeTotalHTML}</tfoot>
            </table>
          </div>
          <div style="font-size:9px;margin-bottom:30px"><span style="font-weight:bold">Remarks: </span>${getRemarks()}</div>
          <div style="display:flex;justify-content:space-between;margin-top:20px">${signaturesHTML}</div>
        </div>`;

      const container = document.createElement('div');
      container.style.cssText = 'position:fixed;top:-99999px;left:-99999px;width:900px;background:white;z-index:-1';
      container.innerHTML = htmlContent;
      document.body.appendChild(container);
      await new Promise(r => setTimeout(r, 300));

      const canvas = await window.html2canvas(container, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false, width: 900, height: container.scrollHeight, windowWidth: 900 });
      document.body.removeChild(container);

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const margin = 10;
      const usableWidth = pdf.internal.pageSize.getWidth() - margin * 2;
      const usableHeight = pdf.internal.pageSize.getHeight() - margin * 2;
      const imgHeightMM = (canvas.height * usableWidth) / canvas.width;
      const pxPerMM = canvas.width / usableWidth;
      let remainingMM = imgHeightMM, sourceYmm = 0;

      while (remainingMM > 0) {
        const sliceHeightMM = Math.min(remainingMM, usableHeight);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = Math.round(sliceHeightMM * pxPerMM);
        const ctx = sliceCanvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(canvas, 0, Math.round(sourceYmm * pxPerMM), canvas.width, sliceCanvas.height, 0, 0, sliceCanvas.width, sliceCanvas.height);
        pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', margin, margin, usableWidth, sliceHeightMM);
        remainingMM -= sliceHeightMM;
        sourceYmm += sliceHeightMM;
        if (remainingMM > 0) pdf.addPage();
      }
      pdf.save(`RR-${rrData.grFull.grNo}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
      Swal.fire({ icon: 'error', title: 'Export Failed', text: 'Could not export PDF.', confirmButtonColor: '#E24B4A' });
    } finally { setExportingPDF(false); }
  };

  // ===== EXPORT EXCEL =====
  const handleExportExcel = async () => {
    if (filteredGRs.length === 0) return;
    setExportingExcel(true);
    try {
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js');
      const XLSX = window.XLSX;
      const rows = filteredGRs.map((gr, idx) => ({
        '#': idx + 1,
        'GR No': gr.grNo || '',
        'GR Date': gr.grDate ? new Date(gr.grDate).toLocaleDateString('en-US') : '',
        'Vendor': resolveVendorName(gr),
        'PO No': gr.poNumber || '',
        'Received By': gr.receivedBy || '',
        'Status': (() => { const map = { 1: 'Active', 2: 'Approved', 3: 'Closed', [-1]: 'Deleted', [-2]: 'Reverted' }; return map[gr.status] || 'Unknown'; })(),
        'JE Status': jeStatusMap[gr.grNo] ? 'Generated' : 'No JE',
      }));
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const colWidths = Object.keys(rows[0] || {}).map(key => ({ wch: Math.max(key.length, ...rows.map(r => String(r[key] || '').length)) + 2 }));
      worksheet['!cols'] = colWidths;
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Receiving Reports');
      XLSX.writeFile(workbook, `Receiving-Reports-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Export Failed', text: 'Could not export Excel.', confirmButtonColor: '#E24B4A' });
    } finally { setExportingExcel(false); }
  };

  const loadScript = (src) => new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const script = document.createElement('script');
    script.src = src; script.onload = resolve; script.onerror = reject;
    document.head.appendChild(script);
  });

  const handleOpenJE = () => {
    if (!rrData) return;
    if (rrData.savedJELines?.length > 0) {
      setJeLines(rrData.savedJELines.map(normalizeJELine));
    } else {
      const itemLines = rrData.mergedItems.map(item => ({
        Account_No: '', Account_Name: item.itemDescription || item.itemCode || '', Debit: item.totalAmt, Credit: 0,
      }));
      setJeLines([...itemLines, { Account_No: '', Account_Name: 'Goods Received Not Invoiced', Debit: 0, Credit: rrData.totalAmount }]);
    }
    setShowJEModal(true);
  };

  const handleJELineChange = (index, field, value) => {
    setJeLines(prev => prev.map((line, i) =>
      i === index ? { ...line, [field]: field === 'Debit' || field === 'Credit' ? parseFloat(value) || 0 : value } : line
    ));
  };

  const handleAddJELine = () => setJeLines(prev => [...prev, { Account_No: '', Account_Name: '', Debit: 0, Credit: 0 }]);
  const handleRemoveJELine = (index) => setJeLines(prev => prev.filter((_, i) => i !== index));

  const totalDebit = jeLines.reduce((sum, l) => sum + (l.Debit || 0), 0);
  const totalCredit = jeLines.reduce((sum, l) => sum + (l.Credit || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const handleSaveJE = async () => {
    if (!isBalanced) { Swal.fire({ icon: 'warning', title: 'Not Balanced', text: 'Debit and Credit totals must match.', confirmButtonColor: '#E24B4A' }); return; }
    if (jeLines.some(l => !l.Account_No.trim() || !l.Account_Name.trim())) { Swal.fire({ icon: 'warning', title: 'Incomplete', text: 'Please fill in all Account No. and Account Name fields.', confirmButtonColor: '#E24B4A' }); return; }
    setSavingJE(true);
    try {
      const grNo = rrData.grFull.grNo;
      let jeCodeNo = '—';
      for (const line of jeLines) {
        const payload = { Gr_No: grNo, RR_no: grNo, Account_No: line.Account_No, Account_Name: line.Account_Name, Debit: parseFloat(line.Debit) || 0, Credit: parseFloat(line.Credit) || 0 };
        const res = await fetch(`${API_BASE_URL}api/JournalEntry`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (!res.ok) { const errText = await res.text(); let errMsg = errText; try { const errBody = JSON.parse(errText); errMsg = errBody?.title || errBody?.message || errText; } catch { } throw new Error(errMsg); }
        if (jeCodeNo === '—') { const data = await res.json(); jeCodeNo = data.jE_Code_No ?? '—'; }
      }
      setSavedJECode(jeCodeNo);
      setJeStatusMap(prev => ({ ...prev, [grNo]: true }));
      await handleViewRR(selectedGR);
      Swal.fire({ icon: 'success', title: 'Saved!', text: 'Journal Entry saved successfully.', confirmButtonColor: '#185FA5' });
      setShowJEModal(false);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: `Failed to save Journal Entry: ${err.message}`, confirmButtonColor: '#E24B4A' });
    } finally { setSavingJE(false); }
  };

  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>Receiving Report - ${rrData?.grFull?.grNo}</title><style>* { margin: 0; padding: 0; box-sizing: border-box; } body { font-family: Arial, sans-serif; font-size: 10px; color: #000; background: #fff; } .rr-wrapper { padding: 20px; max-width: 950px; margin: auto; } .company-header { text-align: center; margin-bottom: 10px; } .items-table { width: 100%; border-collapse: collapse; margin-bottom: 6px; } .items-table th { background: #f0f0f0; border: 1px solid #aaa; padding: 4px 5px; text-align: center; font-size: 8.5px; font-weight: bold; } .items-table td { border: 1px solid #aaa; padding: 3px 5px; font-size: 8.5px; text-align: center; } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }</style></head><body>${printContents}</body></html>`);
    win.document.close(); win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  const formatDate = (d) => { if (!d) return ''; return new Date(d).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }); };
  const formatCurrency = (n) => { if (!n && n !== 0) return '0.00'; return parseFloat(n).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };

  const getStatusBadge = (status) => {
    const map = {
      1: { label: 'Active', style: { background: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' } },
      2: { label: 'Approved', style: { background: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe' } },
      3: { label: 'Closed', style: { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' } },
      [-1]: { label: 'Deleted', style: { background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' } },
      [-2]: { label: 'Reverted', style: { background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa' } },
    };
    const s = map[status] || { label: 'Unknown', style: { background: '#f1f5f9', color: '#94a3b8' } };
    return <span style={{ ...s.style, display: 'inline-block', padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600 }}>{s.label}</span>;
  };

  const getVendorName = () => rrData?.vendorInfo?.VendorName || rrData?.grFull?.vendorName || '—';
  const getVendorTin = () => rrData?.vendorInfo?.TIN || rrData?.grFull?.vendorTin || '';
  const getVendorAddress = () => { const v = rrData?.vendorInfo; if (v) return [v.Address1, v.Address2, v.Address3].filter(Boolean).join(', '); return rrData?.grFull?.vendorAddress || ''; };
  const getShipTo = () => rrData?.grFull?.shipTo || rrData?.grFull?.deliveryAddress || '';
  const getSINo = () => rrData?.grFull?.sI_number || rrData?.grFull?.siNumber || rrData?.grFull?.invoiceNumber || '—';
  const getPoDate = () => rrData?.poHeader?.poDate || rrData?.poHeader?.poCreatedDate || rrData?.poHeader?.deliveryDate || '';
  const getReceivedBy = () => rrData?.grFull?.receivedBy || rrData?.grFull?.preparedBy || '';
  const getRemarks = () => rrData?.grFull?.remarks || '';
  const jeDisplayTotal = (field) => (rrData?.savedJELines || []).reduce((s, je) => { const val = field === 'Debit' ? (je.debit ?? je.Debit) : (je.credit ?? je.Credit); return s + (parseFloat(val) || 0); }, 0);

  // ── Pagination helpers ──
  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) pages.push(i);
      else if (i === currentPage - 2 || i === currentPage + 2) pages.push('...');
    }
    return [...new Set(pages)];
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', padding: '2rem 1.5rem', fontFamily: '"Inter", -apple-system, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }

        .rr-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }

        /* ── Buttons ── */
        .rr-btn { height: 38px; padding: 0 16px; border-radius: 8px; border: none; font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all .2s; font-family: 'Inter', sans-serif; white-space: nowrap; }
        .rr-btn:hover { opacity: .88; transform: translateY(-1px); }
        .rr-btn:active { transform: scale(.97); }
        .rr-btn:disabled { opacity: .45; cursor: not-allowed; transform: none; }
        .rr-btn-ghost { background: #fff; color: #475569; border: 1px solid #e2e8f0; }
        .rr-btn-ghost:hover { background: #f8fafc; }
        .rr-btn-slate { background: #334155; color: #fff; }
        .rr-btn-slate:hover { background: #1e293b; opacity: 1; }
        .rr-btn-green { background: #1D9E75; color: #fff; }
        .rr-btn-green:hover { background: #177a5c; opacity: 1; }
        .rr-btn-red { background: #E24B4A; color: #fff; }
        .rr-btn-red:hover { background: #c03a39; opacity: 1; }
        .rr-btn-blue { background: #185FA5; color: #fff; }
        .rr-btn-blue:hover { background: #0c447c; opacity: 1; }

        /* ── Input ── */
        .rr-input { height: 38px; padding: 0 12px 0 38px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 14px; font-family: 'Inter', sans-serif; color: #1e293b; background: #fff; outline: none; transition: border-color .2s, box-shadow .2s; width: 100%; }
        .rr-input:focus { border-color: #185FA5; box-shadow: 0 0 0 3px rgba(24,95,165,.1); }
        .rr-input::placeholder { color: #94a3b8; }
        .rr-input-wrap { position: relative; }
        .rr-input-wrap svg { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; }

        /* ── Table ── */
        .rr-table { width: 100%; border-collapse: collapse; }
        .rr-table th { background: #f8fafc; padding: .75rem 1rem; text-align: left; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .06em; border-bottom: 1px solid #e2e8f0; white-space: nowrap; }
        .rr-table th.center { text-align: center; }
        .rr-table td { padding: .75rem 1rem; border-bottom: 1px solid #f1f5f9; font-size: 13px; color: #334155; }
        .rr-table tr:last-child td { border-bottom: none; }
        .rr-table tr:hover td { background: #f8fafc; }
        .rr-table .td-center { text-align: center; }

        /* ── Pagination ── */
        .rr-page-btn { height: 34px; min-width: 34px; padding: 0 10px; border: 1px solid #e2e8f0; border-radius: 7px; background: #fff; color: #475569; font-size: 13px; cursor: pointer; transition: all .15s; font-family: 'Inter', sans-serif; display: inline-flex; align-items: center; justify-content: center; }
        .rr-page-btn:hover:not(:disabled) { border-color: #185FA5; color: #185FA5; background: #eff6ff; }
        .rr-page-btn.active { background: #185FA5; border-color: #185FA5; color: #fff; font-weight: 600; }
        .rr-page-btn:disabled { opacity: .35; cursor: not-allowed; }
        .rr-page-ellipsis { height: 34px; min-width: 34px; display: inline-flex; align-items: center; justify-content: center; color: #94a3b8; font-size: 13px; }

        /* ── Stat badge ── */
        .rr-stat { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 99px; font-size: 12px; color: #64748b; font-weight: 500; }
        .rr-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }

        /* ── Spinner ── */
        .rr-spin { animation: rrSpin .7s linear infinite; }
        @keyframes rrSpin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }

        /* ── View btn ── */
        .rr-view-btn { height: 30px; padding: 0 12px; background: #334155; color: #fff; border: none; border-radius: 6px; font-size: 12px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; transition: all .15s; font-family: 'Inter', sans-serif; }
        .rr-view-btn:hover { background: #1e293b; }

        /* ── JE input ── */
        .je-input { width: 100%; border: 1px solid #e2e8f0; border-radius: 6px; padding: 5px 8px; font-size: 12px; font-family: 'Inter', sans-serif; color: #1e293b; outline: none; background: #fff; }
        .je-input:focus { border-color: #1D9E75; box-shadow: 0 0 0 2px rgba(29,158,117,.12); }
        .je-input-disabled { background: #f8fafc; color: #94a3b8; cursor: not-allowed; }

        /* ── Modal ── */
        .rr-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,.5); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 1rem; animation: rrFadeIn .2s ease; }
        @keyframes rrFadeIn { from{opacity:0} to{opacity:1} }
        .rr-modal { background: #fff; border-radius: 16px; box-shadow: 0 20px 60px rgba(0,0,0,.2); width: 100%; display: flex; flex-direction: column; overflow: hidden; animation: rrSlideUp .25s cubic-bezier(.4,0,.2,1); }
        @keyframes rrSlideUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }

        /* ── Icon btn ── */
        .rr-icon-btn { width: 30px; height: 30px; border: none; border-radius: 6px; background: transparent; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; color: #94a3b8; transition: all .15s; }
        .rr-icon-btn:hover { background: #f1f5f9; color: #475569; }
        .rr-icon-btn.danger:hover { background: #fef2f2; color: #E24B4A; }

        .divider { width: 1px; height: 20px; background: #e2e8f0; margin: 0 2px; }
      `}</style>

      <div style={{ maxWidth: 1400, margin: '0 auto' }}>

        {/* ── Header ── */}
        <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ width: 50, height: 50, flexShrink: 0, background: 'linear-gradient(135deg,#334155,#1e293b)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(51,65,85,.25)' }}>
            <FileText size={24} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em', lineHeight: 1.2 }}>Receiving Reports</h1>
            <p style={{ color: '#64748b', fontSize: '.875rem', marginTop: 2 }}>View and print RR from saved Goods Receipts</p>
          </div>
        </div>

        {/* ── Controls Card ── */}
        <div className="rr-card" style={{ padding: '1.25rem 1.5rem', marginBottom: '1.25rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 8, alignItems: 'center' }}>
              <div className="rr-input-wrap">
                <Search size={15} />
                <input
                  type="text"
                  placeholder="Search GR No, Vendor, PO..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="rr-input"
                />
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span className="rr-stat"><span className="rr-dot" style={{ background: '#185FA5' }} />Total: {grList.length}</span>
                <span className="rr-stat"><span className="rr-dot" style={{ background: '#1D9E75' }} />Filtered: {filteredGRs.length}</span>
                <span className="rr-stat"><span className="rr-dot" style={{ background: '#BA7517' }} />Page {currentPage} of {totalPages || 1}</span>
              </div>
            </div>
            <button
              onClick={handleExportExcel}
              disabled={exportingExcel || filteredGRs.length === 0}
              className="rr-btn rr-btn-green"
            >
              {exportingExcel ? <Loader2 size={14} className="rr-spin" /> : <Download size={14} />}
              {exportingExcel ? 'Exporting...' : 'Export Excel'}
            </button>
          </div>
        </div>

        {/* ── Table Card ── */}
        <div className="rr-card" style={{ marginBottom: '1.25rem', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '4rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
              <Loader2 size={28} color="#64748b" className="rr-spin" />
              <p style={{ color: '#94a3b8', fontSize: '.9rem' }}>Loading records...</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="rr-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>GR No</th>
                    <th>GR Date</th>
                    <th>Vendor</th>
                    <th>PO No</th>
                    <th>Received By</th>
                    <th className="center">Status</th>
                    <th className="center">JE</th>
                    <th className="center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedGRs.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ padding: '3.5rem', textAlign: 'center' }}>
                        <Package size={36} color="#cbd5e1" style={{ margin: '0 auto 12px' }} />
                        <p style={{ color: '#94a3b8', fontSize: '.9rem' }}>{search ? 'No records match your search.' : 'No receiving reports found.'}</p>
                      </td>
                    </tr>
                  ) : paginatedGRs.map((gr, idx) => (
                    <tr key={gr.id}>
                      <td style={{ color: '#94a3b8', fontSize: 12 }}>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#334155', fontSize: 13 }}>{gr.grNo}</td>
                      <td style={{ color: '#64748b' }}>{formatDate(gr.grDate)}</td>
                      <td style={{ fontWeight: 500, color: '#0f172a' }}>{resolveVendorName(gr)}</td>
                      <td style={{ color: '#64748b' }}>{gr.poNumber || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ color: '#64748b' }}>{gr.receivedBy || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td className="td-center">{getStatusBadge(gr.status)}</td>
                      <td className="td-center">
                        {jeStatusMap[gr.grNo] === true ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' }}>
                            <CheckCircle size={11} /> Generated
                          </span>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: '#f1f5f9', color: '#94a3b8', border: '1px solid #e2e8f0' }}>
                            <AlertCircle size={11} /> No JE
                          </span>
                        )}
                      </td>
                      <td className="td-center">
                        <button onClick={() => handleViewRR(gr)} className="rr-view-btn">
                          <Eye size={13} /> View RR
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="rr-card" style={{ padding: '1rem 1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="rr-page-btn" title="First">
                <ChevronsLeft size={14} />
              </button>
              <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className="rr-page-btn" title="Previous">
                <ChevronLeft size={14} />
              </button>
              {getPageNumbers().map((p, i) =>
                p === '...'
                  ? <span key={`ellipsis-${i}`} className="rr-page-ellipsis">···</span>
                  : <button key={p} onClick={() => setCurrentPage(p)} className={`rr-page-btn ${currentPage === p ? 'active' : ''}`}>{p}</button>
              )}
              <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} className="rr-page-btn" title="Next">
                <ChevronRight size={14} />
              </button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="rr-page-btn" title="Last">
                <ChevronsRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ===== RR MODAL ===== */}
      {selectedGR && (
        <div className="rr-modal-overlay" onClick={() => { setSelectedGR(null); setRRData(null); setSavedJECode('—'); }}>
          <div className="rr-modal" style={{ maxWidth: 900, maxHeight: '95vh' }} onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <FileText size={18} color="#64748b" />
                <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>Receiving Report</span>
                <span style={{ color: '#94a3b8', fontSize: 14 }}>— {selectedGR.grNo}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {rrData && (
                  <>
                    <button onClick={handleOpenJE} className="rr-btn rr-btn-green" style={{ height: 34, fontSize: 12 }}>
                      <BookOpen size={13} />
                      {rrData.savedJELines?.length > 0 ? 'View / Edit JE' : 'Generate JE'}
                    </button>
                    <button onClick={handleExportPDF} disabled={exportingPDF} className="rr-btn rr-btn-red" style={{ height: 34, fontSize: 12 }}>
                      {exportingPDF ? <Loader2 size={13} className="rr-spin" /> : <Download size={13} />}
                      {exportingPDF ? 'Exporting...' : 'Export PDF'}
                    </button>
                    <button onClick={handlePrint} className="rr-btn rr-btn-slate" style={{ height: 34, fontSize: 12 }}>
                      <Printer size={13} /> Print
                    </button>
                  </>
                )}
                <div className="divider" />
                <button onClick={() => { setSelectedGR(null); setRRData(null); setSavedJECode('—'); }} className="rr-icon-btn">
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ flex: 1, overflowY: 'auto', background: '#f1f5f9', padding: '1.5rem' }}>
              {loadingRR ? (
                <div style={{ padding: '5rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                  <Loader2 size={32} color="#64748b" className="rr-spin" />
                  <p style={{ color: '#94a3b8' }}>Loading report data...</p>
                </div>
              ) : rrData ? (
                <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 820, margin: '0 auto', boxShadow: '0 1px 4px rgba(0,0,0,.06)' }} ref={printRef}>
                  <div className="rr-wrapper">
                    <div className="company-header" style={{ textAlign: 'center', marginBottom: 12 }}>
                      <h1 style={{ fontSize: 13, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1 }}>MEGA SOFT HYGIENIC PRODUCTS, INC.</h1>
                      <p style={{ fontSize: 9, color: '#555', marginTop: 2 }}>Rm202 Mega One Building España Blvd Cor. Mayon St. Brgy Sta. Teresita Quezon City</p>
                      <p style={{ fontSize: 9, color: '#555' }}>Tel No.: 8363-4851 / 8363-4854 / 8363-4853 / 8363-4855</p>
                      <p style={{ fontSize: 9, color: '#555' }}>Vat Reg.: 206-227-179-000</p>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 9, fontWeight: 'bold', marginBottom: 2 }}>RR No.: {rrData.grFull.grNo}</div>
                    <div style={{ textAlign: 'center', fontSize: 12, fontWeight: 'bold', textTransform: 'uppercase', textDecoration: 'underline', letterSpacing: 3, marginBottom: 12 }}>RECEIVING REPORT</div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', border: '1px solid #000', marginBottom: 14, fontSize: 9 }}>
                      <div style={{ borderRight: '1px solid #000', padding: '10px 12px' }}>
                        {[['Vendor Name:', getVendorName()], ['Vendor TIN:', getVendorTin()], ['Vendor Address:', getVendorAddress()], ['Ship To:', getShipTo()]].map(([label, val]) => (
                          <div key={label} style={{ display: 'flex', gap: 6, marginBottom: 5 }}>
                            <span style={{ fontWeight: 'bold', minWidth: 90, whiteSpace: 'nowrap' }}>{label}</span>
                            <span style={{ borderBottom: '1px solid #999', flex: 1, minHeight: 12 }}>{val}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ padding: '10px 12px' }}>
                        {[['PO No.:', rrData.grFull.poNumber ? `Based On PO ${rrData.grFull.poNumber}` : '—'], ['SI No.:', getSINo()], ['PO Date:', formatDate(getPoDate())], ['Document Date:', formatDate(rrData.grFull.grDate)], ['Posting Date:', formatDate(rrData.grFull.postingDate || rrData.grFull.grDate)]].map(([label, val]) => (
                          <div key={label} style={{ display: 'flex', gap: 6, marginBottom: 5 }}>
                            <span style={{ fontWeight: 'bold', minWidth: 100, whiteSpace: 'nowrap' }}>{label}</span>
                            <span style={{ borderBottom: '1px solid #999', flex: 1, minHeight: 12 }}>{val}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ccc', fontSize: 9, marginBottom: 6 }}>
                      <thead>
                        <tr style={{ backgroundColor: '#efefef' }}>
                          {['Item Code', 'Item Description', 'PO Qty', 'UoM', 'Gross Price', 'Amt w/o Vat', 'Vat Amt', 'Disc', 'Total Amt', 'Qty Rcvd', 'Qty Diff'].map(h => (
                            <th key={h} style={{ border: '1px solid #bbb', padding: '5px 6px', textAlign: 'center', fontWeight: 600, color: '#333', whiteSpace: 'nowrap', backgroundColor: '#efefef' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rrData.mergedItems.map((item, i) => (
                          <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                            <td style={{ border: '1px solid #ccc', padding: '4px 6px', fontFamily: 'monospace' }}>{item.itemCode}</td>
                            <td style={{ border: '1px solid #ccc', padding: '4px 6px', textTransform: 'uppercase' }}>{item.itemDescription}</td>
                            <td style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'center' }}>{formatCurrency(item.poQty)}</td>
                            <td style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'center', textTransform: 'uppercase' }}>{item.uom}</td>
                            <td style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'right' }}>{formatCurrency(item.grossPrice)}</td>
                            <td style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'right' }}>{formatCurrency(item.amtWoVat)}</td>
                            <td style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'right' }}>{formatCurrency(item.vatAmt)}</td>
                            <td style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'right' }}>{formatCurrency(item.disc)}</td>
                            <td style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(item.totalAmt)}</td>
                            <td style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'center' }}>{formatCurrency(item.qtyRcvd)}</td>
                            <td style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'center' }}>{formatCurrency(item.qtyDiff)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 9, margin: '8px 0' }}>****NOTHING FOLLOWS***</div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 32, borderTop: '1px solid #000', paddingTop: 6, marginBottom: 14, fontSize: 9, fontWeight: 'bold' }}>
                      <span style={{ textDecoration: 'underline' }}>Total Amount</span>
                      <span>{formatCurrency(rrData.totalAmount)}</span>
                    </div>

                    <div style={{ border: '1px solid #000', padding: '10px 12px', marginBottom: 14, fontSize: 9 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontWeight: 'bold' }}>JE No. {savedJECode}</span>
                        <span style={{ fontWeight: 'bold', flex: 1, textAlign: 'center' }}>Journal Entry</span>
                      </div>
                      <table style={{ width: '100%', fontSize: 9 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #000' }}>
                            <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 'bold' }}>Account</th>
                            <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 'bold' }}>Account Name</th>
                            <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 'bold' }}>Debit</th>
                            <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 'bold' }}>Credit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rrData.savedJELines?.length > 0 ? rrData.savedJELines.map((je, i) => {
                            const norm = normalizeJELine(je);
                            return (
                              <tr key={i}>
                                <td style={{ padding: '3px 6px' }}>{norm.Account_No}</td>
                                <td style={{ padding: '3px 6px' }}>{norm.Account_Name}</td>
                                <td style={{ padding: '3px 6px', textAlign: 'right' }}>{formatCurrency(norm.Debit)}</td>
                                <td style={{ padding: '3px 6px', textAlign: 'right' }}>{formatCurrency(norm.Credit)}</td>
                              </tr>
                            );
                          }) : (
                            <tr><td colSpan={4} style={{ padding: '8px', textAlign: 'center', color: '#aaa', fontStyle: 'italic' }}>No Journal Entry generated yet.</td></tr>
                          )}
                        </tbody>
                      </table>
                      {rrData.savedJELines?.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #000', paddingTop: 4, marginTop: 4, fontWeight: 'bold', fontSize: 9 }}>
                          <span>TOTAL</span>
                          <div style={{ display: 'flex', gap: 32 }}>
                            <span>{formatCurrency(jeDisplayTotal('Debit'))}</span>
                            <span>{formatCurrency(jeDisplayTotal('Credit'))}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div style={{ fontSize: 9, marginBottom: 24 }}>
                      <span style={{ fontWeight: 'bold' }}>Remarks: </span>{getRemarks()}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginTop: 20, fontSize: 9 }}>
                      {[{ label: 'Prepared By:', name: getReceivedBy() }, { label: 'Noted By:', name: '-' }, { label: 'Checked By:', name: '-' }, { label: 'Approved By:', name: '-' }].map((sig, i) => (
                        <div key={i} style={{ textAlign: 'center' }}>
                          <div style={{ textAlign: 'left', fontWeight: 'bold', marginBottom: 20 }}>{sig.label}</div>
                          {sig.name && <div style={{ fontWeight: 600, textAlign: 'center', fontSize: 9 }}>{sig.name}</div>}
                          <div style={{ borderTop: '1px solid #000', paddingTop: 3, fontWeight: 'bold', textAlign: 'center', marginTop: 24, fontSize: 9 }}>{sig.name || '\u00A0'}</div>
                          <div style={{ color: '#666', textAlign: 'center', fontSize: 8 }}>{sig.label.replace(':', '')}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* ===== JE MODAL ===== */}
      {showJEModal && (
        <div className="rr-modal-overlay" style={{ zIndex: 60 }} onClick={() => setShowJEModal(false)}>
          <div className="rr-modal" style={{ maxWidth: 780, maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
            {/* JE Header */}
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <BookOpen size={18} color="#1D9E75" />
                <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>
                  {rrData?.savedJELines?.length > 0 ? 'View / Edit Journal Entry' : 'Generate Journal Entry'}
                </span>
                <span style={{ color: '#94a3b8', fontSize: 14 }}>— {rrData?.grFull?.grNo}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '4px 12px', borderRadius: 99 }}>
                  JE No. {savedJECode !== '—' ? savedJECode : 'auto-generated'}
                </span>
                <button onClick={() => setShowJEModal(false)} className="rr-icon-btn"><X size={18} /></button>
              </div>
            </div>

            {/* JE Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: '1.25rem' }}>
                {[['GR No.', rrData?.grFull?.grNo], ['RR No.', rrData?.grFull?.grNo]].map(([label, val]) => (
                  <div key={label}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 5 }}>{label}</label>
                    <input type="text" value={val || ''} disabled className="je-input je-input-disabled" />
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Journal Entry Lines</span>
                  <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>— fill in Account No. per row</span>
                </div>
                <button onClick={handleAddJELine} className="rr-btn rr-btn-green" style={{ height: 32, fontSize: 12 }}>
                  <Plus size={13} /> Add Row
                </button>
              </div>

              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: '1rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      {['Account No.', 'Account Name', 'Debit', 'Credit', ''].map((h, i) => (
                        <th key={i} style={{ padding: '10px 12px', textAlign: i >= 2 && i < 4 ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', borderRight: i < 4 ? '1px solid #e2e8f0' : 'none', width: i === 4 ? 40 : 'auto' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {jeLines.map((line, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafbfd' }}>
                        <td style={{ padding: '6px 8px', borderRight: '1px solid #e2e8f0', width: 140 }}>
                          <input type="text" value={line.Account_No} onChange={e => handleJELineChange(idx, 'Account_No', e.target.value)} placeholder="e.g. 101050705" className="je-input" />
                        </td>
                        <td style={{ padding: '6px 8px', borderRight: '1px solid #e2e8f0' }}>
                          <input type="text" value={line.Account_Name} onChange={e => handleJELineChange(idx, 'Account_Name', e.target.value)} placeholder="Account Name" className="je-input" />
                        </td>
                        <td style={{ padding: '6px 8px', borderRight: '1px solid #e2e8f0', width: 120 }}>
                          <input type="number" value={line.Debit} onChange={e => handleJELineChange(idx, 'Debit', e.target.value)} min="0" step="0.01" className="je-input" style={{ textAlign: 'right' }} />
                        </td>
                        <td style={{ padding: '6px 8px', borderRight: '1px solid #e2e8f0', width: 120 }}>
                          <input type="number" value={line.Credit} onChange={e => handleJELineChange(idx, 'Credit', e.target.value)} min="0" step="0.01" className="je-input" style={{ textAlign: 'right' }} />
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'center', width: 40 }}>
                          <button onClick={() => handleRemoveJELine(idx)} disabled={jeLines.length <= 1} className="rr-icon-btn danger" style={{ width: 26, height: 26 }}>
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                      <td colSpan={2} style={{ padding: '10px 12px', fontWeight: 700, fontSize: 12, color: '#475569', borderRight: '1px solid #e2e8f0' }}>TOTAL</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, fontSize: 12, textAlign: 'right', color: '#185FA5', borderRight: '1px solid #e2e8f0' }}>{formatCurrency(totalDebit)}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, fontSize: 12, textAlign: 'right', color: '#185FA5', borderRight: '1px solid #e2e8f0' }}>{formatCurrency(totalCredit)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {!isBalanced ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px' }}>
                  <AlertCircle size={14} /> Not balanced. Difference: {formatCurrency(Math.abs(totalDebit - totalCredit))}
                </div>
              ) : jeLines.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#065f46', background: '#d1fae5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '8px 12px' }}>
                  <CheckCircle size={14} /> Balanced — Debit equals Credit.
                </div>
              )}
            </div>

            {/* JE Footer */}
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 8, justifyContent: 'flex-end', flexShrink: 0 }}>
              <button onClick={() => setShowJEModal(false)} className="rr-btn rr-btn-ghost">Cancel</button>
              <button onClick={handleSaveJE} disabled={savingJE || !isBalanced} className="rr-btn rr-btn-green">
                {savingJE ? <Loader2 size={14} className="rr-spin" /> : <BookOpen size={14} />}
                {savingJE ? 'Saving...' : 'Save Journal Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}